// Oike — test records in the real base ("PRUEBA — …"), kept out of every
// commercial screen, report and metric.
//
// There is only one base, so the acceptance checks create fictitious records in
// it (scripts/person-access-real-check.mjs). They are identified by their name,
// which starts with TEST_MARK, and by their links: a contact or an opportunity
// of a test company, and an interaction of a test contact, are test records too.
// The commercial loader (hooks/useAppData.js) keeps two views: `data`, with
// everything, for the screens where records are found and worked on (Contactos,
// Empresas, Solicitudes, the search), so the test person, company, contact and
// opportunity can be opened and edited during a validation; and `reportingData`,
// without them, for every metric, report, pipeline, follow-up list, campaign,
// event and message generator, so they are never counted or targeted. Personas
// reads from the server and always shows them, with a "Registro de prueba" mark.
// Diagnostics use their own flag (Is Test).
// Pure: the tests use it.
export const TEST_MARK = 'PRUEBA — ';

const text = (r, f) => { const v = r?.fields?.[f]; return typeof v === 'string' ? v : v?.name || ''; };
const links = (r, f) => (Array.isArray(r?.fields?.[f]) ? r.fields[f].filter(x => typeof x === 'string') : []);
export const isTestName = (s) => String(s || '').trim().startsWith(TEST_MARK);

/** The ids of the test records, by table. */
export function testRecordIds(results) {
  const accounts = new Set((results.accounts || []).filter(a => isTestName(text(a, 'Account Name'))).map(a => a.id));
  const contactName = (s) => [text(s, 'Name'), text(s, 'Last name')].filter(Boolean).join(' ');
  const stakeholders = new Set((results.stakeholders || [])
    .filter(s => isTestName(contactName(s)) || links(s, 'Account').some(id => accounts.has(id))).map(s => s.id));
  const opportunities = new Set((results.opportunities || [])
    .filter(o => isTestName(text(o, 'Deal/Opp name')) || links(o, 'Account').some(id => accounts.has(id)) || links(o, 'Contact').some(id => stakeholders.has(id))).map(o => o.id));
  const outreach = new Set((results.outreach || [])
    .filter(o => [...links(o, 'Stakeholder'), ...links(o, 'Stakeholders')].some(id => stakeholders.has(id))).map(o => o.id));
  return { accounts, stakeholders, opportunities, outreach };
}

/** A copy of the data without the test records (for metrics, reports, campaigns…), and how many there were. */
export function withoutTestRecords(data) {
  const ids = testRecordIds(data);
  const out = { ...data };
  const counts = {};
  for (const key of Object.keys(ids)) {
    const before = data[key] || [];
    out[key] = before.filter(r => !ids[key].has(r.id));
    counts[key] = before.length - out[key].length;
  }
  out.testRecords = counts;
  return out;
}
